import { useEffect, useMemo, useState } from 'react'
import { Column } from './types'

interface UseColumnVisibilityProps {
  columns: Column[]
  projectGroupId: number
}

interface UseColumnVisibilityResult {
  visibleColumnKeys: Set<string>
  visibleColumns: Column[]
  setVisibleColumnKeys: (keys: Set<string>) => void
}

interface StoredColumnVisibility {
  visibleKeys: string[]
  knownKeys: string[]
}

const storageKey = (projectGroupId: number): string => `pgColumns:${projectGroupId}`

const areSetsEqual = (left: Set<string>, right: Set<string>): boolean => {
  if (left.size !== right.size) return false

  for (const value of left) {
    if (!right.has(value)) return false
  }

  return true
}

const readStoredColumnState = (projectGroupId: number): StoredColumnVisibility | null => {
  try {
    const value = window.localStorage.getItem(storageKey(projectGroupId))
    if (!value) return null

    const parsed = JSON.parse(value)

    if (Array.isArray(parsed)) {
      const visibleKeys = parsed.filter((key): key is string => typeof key === 'string')
      return { visibleKeys, knownKeys: visibleKeys }
    }

    if (typeof parsed !== 'object' || parsed == null) return null

    const visibleKeys = Array.isArray(parsed.visibleKeys)
      ? parsed.visibleKeys.filter((key): key is string => typeof key === 'string')
      : []
    const knownKeys = Array.isArray(parsed.knownKeys)
      ? parsed.knownKeys.filter((key): key is string => typeof key === 'string')
      : []

    return { visibleKeys, knownKeys }
  } catch {
    return null
  }
}

const normalizeVisibleColumnKeys = (
  columnKeys: string[],
  storedState: StoredColumnVisibility | null,
): Set<string> => {
  if (columnKeys.length === 0) return new Set()

  if (!storedState) {
    return new Set(columnKeys)
  }

  const visibleKeys = new Set(storedState.visibleKeys)
  const knownKeys = new Set(storedState.knownKeys)
  const normalizedKeys = new Set(columnKeys.filter((key) => (
    visibleKeys.has(key) || !knownKeys.has(key)
  )))

  if (normalizedKeys.size === 0) {
    return new Set(columnKeys)
  }

  return normalizedKeys
}

export default function useColumnVisibility({
  columns,
  projectGroupId,
}: UseColumnVisibilityProps): UseColumnVisibilityResult {
  const columnKeys = useMemo(() => columns.map((column) => column.key), [columns])
  const [visibleColumnKeys, setVisibleColumnKeysState] = useState<Set<string>>(() =>
    normalizeVisibleColumnKeys(columnKeys, readStoredColumnState(projectGroupId))
  )

  useEffect(() => {
    const normalizedKeys = normalizeVisibleColumnKeys(columnKeys, readStoredColumnState(projectGroupId))
    setVisibleColumnKeysState((currentKeys) => (
      areSetsEqual(currentKeys, normalizedKeys) ? currentKeys : normalizedKeys
    ))
  }, [projectGroupId, columnKeys])

  useEffect(() => {
    window.localStorage.setItem(
      storageKey(projectGroupId),
      JSON.stringify({
        visibleKeys: columnKeys.filter((key) => visibleColumnKeys.has(key)),
        knownKeys: columnKeys,
      })
    )
  }, [projectGroupId, columnKeys, visibleColumnKeys])

  const visibleColumns = useMemo(
    () => columns.filter((column) => visibleColumnKeys.has(column.key)),
    [columns, visibleColumnKeys]
  )

  const setVisibleColumnKeys = (keys: Set<string>) => {
    const normalizedKeys = new Set(columnKeys.filter((key) => keys.has(key)))
    setVisibleColumnKeysState((currentKeys) => (
      areSetsEqual(currentKeys, normalizedKeys) ? currentKeys : normalizedKeys
    ))
  }

  return {
    visibleColumnKeys,
    visibleColumns,
    setVisibleColumnKeys,
  }
}
